import { Injectable } from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from "@angular/common/http";
import { Observable } from "rxjs";
import { KeycloakService } from "keycloak-angular";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private loggingIn = false; // evita múltiples redirecciones
  private static readonly RELOAD_FLAG = 'AUTH_RELOAD_ONCE';

  constructor(private kc: KeycloakService) {
    // Si venimos de un reload forzado, limpia el flag para no entrar en bucle
    if (sessionStorage.getItem(AuthInterceptor.RELOAD_FLAG)) {
      sessionStorage.removeItem(AuthInterceptor.RELOAD_FLAG);
    }
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isStaticAssetRequest(req.url)) {
      return next.handle(req);
    }

    return new Observable((observer) => {
      this.ensureActiveToken()
        .then((token) => {
          const forward = (r: HttpRequest<unknown>) =>
            next.handle(r).subscribe({
              next: (event) => observer.next(event),
              error: (err) => {
                if (this.shouldForceLogin(err)) {
                  // Forzar login (sin hardcode de rutas): volveremos a la URL donde caducó
                  this.gotoLogin();
                  observer.complete();
                  return;
                }
                observer.error(err);
              },
              complete: () => observer.complete(),
            });

          const req2 = token
            ? req.clone({ headers: req.headers.set("Authorization", `Bearer ${token}`) })
            : req;

          forward(req2);
        })
        .catch(() => {
          // No se pudo refrescar/obtener token => ir a login
          this.gotoLogin();
          observer.complete();
        });
    });
  }

  private isStaticAssetRequest(url: string): boolean {
    if (!url) {
      return false;
    }
    return url.includes("/assets/") || url.startsWith("assets/");
  }

  /** Intenta refrescar el token si caduca en <30s y devuelve el token activo. */
  private async ensureActiveToken(): Promise<string | null> {
    try {
      await this.kc.updateToken(30);
      return await this.kc.getToken();
    } catch {
      return null;
    }
  }

  /** Decide si debemos forzar login por el error recibido. */
  private shouldForceLogin(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) return false;

    if (this.isInvalidGrantTokenNotActive(err)) return true;

    if (err.status === 401 || err.status === 403) {
      const hdr = err.headers?.get('WWW-Authenticate') || '';
      const body = typeof err.error === 'string' ? err.error : JSON.stringify(err.error || {});
      if (/invalid_token|expired|not active|signature|bearer/i.test(hdr + ' ' + body)) {
        return true;
      }
    }
    return false;
  }

  private isInvalidGrantTokenNotActive(err: HttpErrorResponse): boolean {
    const body: any = err.error;
    const is400 = err.status === 400;
    const isTokenEndpoint = typeof err.url === "string" && err.url.includes("/protocol/openid-connect/token");
    const isInvalidGrant = body && body.error === "invalid_grant";
    const isNotActive =
      typeof body?.error_description === "string" &&
      body.error_description.trim().toLowerCase() === "token is not active";
    return is400 && isInvalidGrant && isNotActive && isTokenEndpoint;
  }

  /**
   * Redirige al login de Keycloak y, tras autenticarse, vuelve a la URL exacta
   * donde se produjo la caducidad del token (cualquier ruta de la app).
   * Evita hardcodear '/home/parametrizacion-sistema'.
   */
  private gotoLogin(): void {
    if (this.loggingIn) return;
    this.loggingIn = true;

    // La URI de retorno será exactamente la URL actual (incluye path y query/fragment)
    const redirectUri = window.location.href;

    // Usa el SDK (delegando en PKCE/params correctos)
    this.kc.login({ redirectUri }).catch(() => {
      // Fallback último recurso: recarga para que el initializer/guards relancen el flujo
      this.hardReloadOnce();
    });
  }

  /** Realiza un hard reload una única vez para evitar bucles. */
  private hardReloadOnce(): void {
    if (!sessionStorage.getItem(AuthInterceptor.RELOAD_FLAG)) {
      sessionStorage.setItem(AuthInterceptor.RELOAD_FLAG, '1');
      window.location.reload();
    }
  }
}
